import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';
import { Country } from './country.js';

export class Locality extends DataModel {
  public static readonly TYPE_ID: string = 'f20a276171d545ea8f18e435fff7761c';
  public static readonly SCHEMA_VERSION: number = 2;

  public readonly formattedName: string;

  constructor(
    public readonly uniqueId: string,
    public readonly name: string,
    public readonly countyId: string,
    public readonly county: string,
    public readonly country: Country,
  ) {
    super();

    this.formattedName = `${name}, ${county}`;
  }

  get $typeId(): string {
    return Locality.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(Locality.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): Locality {
    return new Locality(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'name'),
        MapUtils.get<string>(map, 'countyId'),
        MapUtils.get<string>(map, 'county'),
        toEnumFromString(MapUtils.get<string>(map, 'country'), Country),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'name': this.name,
      'countyId': this.countyId,
      'county': this.county,
      'country': fromEnumAsString(this.country),
    };
  }

  clone(): this {
    return new Locality(
        this.uniqueId,
        this.name,
        this.countyId,
        this.county,
        this.country,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.name === this.name &&
        other.countyId === this.countyId &&
        other.country === this.county &&
        other.country === this.country;
  }
}

export class LocalityBuilder extends DataModelBuilder<Locality> {
  build(context: SerializationContext, map: SerializedData): Locality {
    return Locality.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(Locality.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(Locality.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.set(map, 'country', MapUtils.get<string>(map, 'uniqueId').substring(0, 2).toUpperCase());
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
